import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, type LucideIcon } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils';

export interface JumpItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

/** "Jump to a page" palette (design `.jump`). Opens with Cmd/Ctrl+K. */
export function CommandPalette({
  open,
  onOpenChange,
  items,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: JumpItem[];
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? items.filter((i) => i.name.toLowerCase().includes(q)) : items;
  }, [items, query]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  useEffect(() => setActive(0), [query]);

  const go = (item: JumpItem | undefined) => {
    if (!item) return;
    onOpenChange(false);
    navigate(item.href);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active]);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className="fixed left-1/2 top-[12vh] z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-card shadow-lg"
          onKeyDown={onKeyDown}
        >
          <DialogPrimitive.Title className="sr-only">Jump to a page</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Type to filter pages, use the arrow keys to move and Enter to open.
          </DialogPrimitive.Description>
          <div className="flex items-center gap-2.5 border-b border-border px-3.5">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Jump to a page…"
              autoComplete="off"
              aria-label="Jump to a page"
              className="h-12 w-full bg-transparent text-[14.5px] outline-none placeholder:text-muted-foreground"
            />
          </div>
          <ul role="listbox" aria-label="Pages" className="max-h-[50vh] overflow-y-auto p-1.5">
            {results.length === 0 && (
              <li className="px-3 py-3 text-[13px] text-muted-foreground">No pages match.</li>
            )}
            {results.map((item, i) => (
              <li key={item.href} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  onClick={() => go(item)}
                  onMouseMove={() => setActive(i)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[13.5px]',
                    i === active && 'bg-foreground/[0.06]'
                  )}
                >
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {item.name}
                </button>
              </li>
            ))}
          </ul>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
